import React from "react"
import {
  FIRESTONE,
  HIBDON_TIRES_PLUS,
  MICHEL_TIRES_PLUS,
  TIRES_PLUS,
  WHEEL_WORKS,
  JIFFY_LUBE_SHOP,
  VALVOLINE_SHOP,
  TAKE_5_SHOP
} from "../constants/shops"
import { isNull, isUndefined } from "lodash"

import i18n from "../utilities/i18n"
import { formatCurrency } from "../utilities/currency"
import { some } from "lodash"
import { MD_HD_VEHICLE_TYPE_ID } from "../constants/application"

export function formattedAddress(shop) {
  if (shop == null) return ""

  let address = ""

  if (shop.address_line1) {
    address += `${shop.address_line1}, `
  }
  if (shop.city) {
    address += `${shop.city} `
  }
  if (shop.state) {
    address += `${shop.state} `
  }
  return (address += `${shop.zip}`)
}

export function formattedName(shop, custom_shop_name) {
  if (custom_shop_name) return custom_shop_name
  if (!shop) return null

  let name = `${shop.name}`
  if (shop.city && shop.state) name += ` ${shop.city}, ${shop.state}`
  return name
}

export function googleMapLink(shop) {
  const { address_line1, city, state, zip, lat, lng } = shop

  const formattedAddress = address_line1 && address_line1.replace(/\s/g, "+")

  return formattedAddress
    ? `https://www.google.com/maps/place/${formattedAddress},+${city},+${state}+${zip}`
    : lat && lng
    ? `http://maps.google.com/?q=${lat},${lng}`
    : ""
}

export function selectOptions(shops, options = {}) {
  let vehicle = options.vehicle
  let shopOptions
  if (vehicle && vehicle.vehicle_type_id === MD_HD_VEHICLE_TYPE_ID) {
    shopOptions = options.includeBlank
      ? [
          {
            value: null,
            label: i18n.t("common:mdHdShopsLabel")
          }
        ]
      : []
  } else {
    shopOptions = options.includeBlank ? [{ value: null, label: "" }] : []
  }
  shops.forEach((shop) => {
    shopOptions.push({
      label: `${shop.name} ${
        shop.address_line1 ? `(${shop.address_line1} ${shop.city} ${shop.state})` : ""
      }`,
      value: shop.id
    })
  })

  return shopOptions
}

export function UnavailableCompanyMessage(company, t) {
  if (company.walk_ins_only) {
    return (
      <div>
        <strong>
          <p style={{ color: "#FF0000" }}>{t("noAppointmentMessage")}</p>
        </strong>
        <br />
      </div>
    )
  }
}

// ==============Code Taken from Retail App Start==================================

const textifiedPrice = ({ price, company, vehicle_type_id }) => {
  if (vehicle_type_id == 10) {
    return noPriceLabel(company, vehicle_type_id)
  }
  return [null, undefined, ""].includes(price) ? noPriceLabel(company) : formatCurrency(price)
}

const textifiedRetailPrice = ({ price, savings, company, vehicle_type_id }) => {
  if (vehicle_type_id == 10) {
    return null
  }
  return [null, undefined, ""].includes(price) ? null : formatCurrency(price + savings)
}

const summedEstimateConsideringNull = (priceEstimates, attr) => {
  if (
    priceEstimates == null ||
    priceEstimates.length === 0 ||
    some(priceEstimates, (pe) => pe[attr] == null)
  )
    return null

  return priceEstimates.reduce((acc, val) => {
    return acc + Number(val[attr])
  }, 0)
}

// The API returns one price_estimate per requested (service_definition_id, position) pair. When a
// service was requested without a position, the API expands it into one estimate per available
// position so the front end can pick the right one. Without this filter, summing shop.price_estimates
// directly adds up every position's price instead of just the one the user actually selected.
const relevantPriceEstimates = (priceEstimates, servicesRequested) => {
  if (!servicesRequested || servicesRequested.length === 0) return priceEstimates

  return servicesRequested
    .map((requested) => {
      const matches = (priceEstimates || []).filter(
        (pe) => pe.service_definition_id === requested.service_definition_id
      )
      return requested.position
        ? matches.find((pe) => pe.position === requested.position)
        : matches[0]
    })
    .filter(Boolean)
}

export function withPricing(shops, servicesRequested = []) {
  return (shops || []).map((shop) => {
    const priceEstimates = relevantPriceEstimates(shop.price_estimates, servicesRequested)
    const shopPriceEstimate = summedEstimateConsideringNull(priceEstimates, "shop_price_estimate")
    const retailPriceEstimate = summedEstimateConsideringNull(
      priceEstimates,
      "retail_price_estimate"
    )
    const retailSavings = (shop.company || {}).retail_savings
    const shopSavings = savingsFromDiscount(shopPriceEstimate, retailSavings)

    return {
      ...shop,
      shopPriceEstimate: shopPriceEstimate,
      shopPriceText: textifiedPrice({
        price: shopPriceEstimate,
        company: shop.company,
        vehicle_type_id: shop.vehicle_type_id
      }),
      shopSavings: shopSavings,
      shopSavingsText: formatCurrency(shopSavings),
      retailPriceEstimate: retailPriceEstimate,
      retailPriceText: textifiedRetailPrice({
        price: shopPriceEstimate,
        savings: shop.total_retail_savings,
        company: shop.company,
        vehicle_type_id: shop.vehicle_type_id
      })
    }
  })
}

export function savingsFromDiscount(price, discount) {
  if (!price || !discount) return null

  const decimalSavings = parseFloat(discount) / 100.0
  // This gives the savings that are `discount` percent less than some original price which we back into.
  return price * (decimalSavings / (1 - decimalSavings))
}

export function noPriceLabel(company = {}, vehicle_type_id) {
  if (vehicle_type_id && vehicle_type_id === 10) {
    return i18n.t("common:priceAtShopLbl")
  } else {
    return company.pricing_disabled && company.retail_savings
      ? i18n.t("common:percentOffRetailLbl", { percent: parseInt(company.retail_savings) })
      : i18n.t("common:priceAtShopLbl")
  }
}

export const getShopAddress = (shop = {}) => {
  const addressParts = [shop.address_line1, shop.city, shop.state, shop.zip].filter(
    (part) => !isNull(part) && !isUndefined(part)
  )

  return addressParts.join(", ")
}
